const SELECTED_SUBTENANT_KEY = 'rbac_selected_subtenant_id';
const SELECTED_SUBTENANT_MEMBER_KEY = 'rbac_selected_subtenant_member_external_user_id';
const ROOT_CONTEXT_ACTIVE_KEY = 'rbac_root_context_active';
const OWNER_ACCOUNT_ID_KEY = 'rbac_owner_account_id';
const CURRENT_ACCOUNT_ID_KEY = 'rbac_current_account_id';

export function readActiveTenantScope() {
  const activeAccountId = readPositiveInt(SELECTED_SUBTENANT_KEY);
  const activeUserId = readPositiveInt(SELECTED_SUBTENANT_MEMBER_KEY);
  const rootContextActive = isRootContextActive();
  // Root org context intentionally leaves selected-subtenant null (so permissions stay
  // org-owner ["*"]). Resolve the root/owner account id for data-scoping headers.
  const rootListingAccountId =
    rootContextActive && activeAccountId == null ? readRootListingAccountId() : null;

  return {
    activeAccountId,
    activeUserId,
    rootListingAccountId,
    isAggregateSubTenantScope: activeAccountId != null || rootListingAccountId != null,
  };
}

export function getActiveTenantHeaders(): Record<string, string> {
  const impersonating = isImpersonating();
  const { activeAccountId, activeUserId, rootListingAccountId } = readActiveTenantScope();
  const headers: Record<string, string> = {};

  const accountId = activeAccountId ?? rootListingAccountId;
  if (accountId != null) headers['X-Active-Account-Id'] = String(accountId);
  // Skip X-Active-User-Id when impersonating — the member context belongs to the admin's
  // session, not the impersonated user's.
  if (!impersonating && activeUserId != null) headers['X-Active-User-Id'] = String(activeUserId);

  return headers;
}

export function resolveActiveRequestUserId(authUserId?: string | number | null): string | number | null {
  if (isImpersonating()) return authUserId ?? null;

  const { activeUserId } = readActiveTenantScope();
  if (activeUserId != null) return activeUserId;
  return authUserId ?? null;
}

function isImpersonating(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem('impersonation') === 'true';
  } catch {
    return false;
  }
}

function isRootContextActive(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(ROOT_CONTEXT_ACTIVE_KEY) === '1';
  } catch {
    return false;
  }
}

function readRootListingAccountId(): number | null {
  return readPositiveInt(OWNER_ACCOUNT_ID_KEY) ?? readPositiveInt(CURRENT_ACCOUNT_ID_KEY);
}

function readPositiveInt(key: string): number | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
}
